import { IaChatAnswer } from './ia-chat-answer.parser';

/**
 * Roteiro de perguntas do ORÇAMENTO — a máquina de estados, sem UI.
 *
 * Espelho de `ia-chat-quote-script.dart` (app): mesmas etapas, mesmas transições.
 * O chat é uma coleta local (nada é gerado durante a conversa), então o roteiro
 * é só isto — um estágio atual e as regras de transição. Ele NÃO extrai valores:
 * apenas marca cada fala com o tópico ativo e evita perguntar o que o usuário já
 * respondeu.
 *
 * Vale só para `DocumentTypeEnum.QUOTE`; os outros seguem em conversa livre.
 */
export type IaChatQuoteStage =
    /** "Quais produtos ou serviços vão estar no orçamento?" */
    | 'items'
    /** "Esse orçamento vai ter taxas?" — botões Sim/Não. */
    | 'taxesAsk'
    /** "Qual taxa e quanto?" — disse que tem, mas não deu o valor. */
    | 'taxesValue'
    /** "Esse valor é em R$ ou %?" — só quando veio número sem unidade. */
    | 'taxesMode'
    | 'discountAsk'
    | 'discountValue'
    | 'discountMode'
    /** Roteiro encerrado — a conversa volta a ser livre. */
    | 'done';

/** Tópico enviado ao backend junto da fala (espelha o IaChatTopicEnum da API). */
export type IaChatTopic = 'items' | 'taxes' | 'discount';

/** Modo de um valor de taxa/desconto quando escolhido pelos botões. */
export type IaChatAmountMode = 'fixed' | 'percentage';

/** Resposta de um botão do roteiro. */
export type IaChatQuickReply = 'yes' | 'no' | 'fixed' | 'percentage';

/**
 * Opção pronta para a tela — o rótulo depende do idioma e da moeda da empresa.
 *
 * `value` é string (e não `IaChatQuickReply`) porque a mesma lista serve aos
 * botões do roteiro local (enum) e aos sugeridos pela IA, onde o rótulo é a
 * própria resposta.
 */
export interface IaChatQuickReplyOption {
    value: string;
    label: string;
}

/** Tópico com que as falas de um estágio são marcadas (null = conversa livre). */
export function topicOfStage(stage: IaChatQuoteStage): IaChatTopic | null {
    switch (stage) {
        case 'items':
            return 'items';
        case 'taxesAsk':
        case 'taxesValue':
        case 'taxesMode':
            return 'taxes';
        case 'discountAsk':
        case 'discountValue':
        case 'discountMode':
            return 'discount';
        default:
            return null;
    }
}

/** Botões que o estágio oferece (vazio = só texto livre). */
export function quickRepliesOfStage(stage: IaChatQuoteStage): IaChatQuickReply[] {
    switch (stage) {
        case 'taxesAsk':
        case 'discountAsk':
            return ['yes', 'no'];
        case 'taxesMode':
        case 'discountMode':
            return ['fixed', 'percentage'];
        default:
            return [];
    }
}

/**
 * Próximo estágio depois de uma resposta ESCRITA.
 *
 * A regra que dá nome ao roteiro: nunca perguntar o que a resposta já disse. Um
 * "Sim, taxa de R$ 10" pula direto para o desconto — a pergunta do modo só
 * aparece quando veio número sem unidade ("taxa de visita, 50").
 */
export function nextStageFromText(stage: IaChatQuoteStage, answer: IaChatAnswer): IaChatQuoteStage {
    switch (stage) {
        case 'items':
            return 'taxesAsk';

        case 'taxesAsk':
            if (answer.isNegative) return 'discountAsk';
            if (answer.needsMode) return 'taxesMode';
            if (answer.hasExplicitMode) return 'discountAsk';
            // Disse que tem ("sim") ou nomeou a taxa sem valor: falta o quanto.
            return 'taxesValue';

        case 'taxesValue':
            // Aqui não insistimos: com número sem unidade perguntamos o modo, e em
            // qualquer outro caso seguimos — quem interpreta o resto é a IA.
            return answer.needsMode ? 'taxesMode' : 'discountAsk';

        case 'taxesMode':
            return 'discountAsk';

        case 'discountAsk':
            if (answer.isNegative) return 'done';
            if (answer.needsMode) return 'discountMode';
            if (answer.hasExplicitMode) return 'done';
            return 'discountValue';

        case 'discountValue':
            return answer.needsMode ? 'discountMode' : 'done';

        default:
            return 'done';
    }
}

/** Próximo estágio depois de um clique em botão. */
export function nextStageFromReply(stage: IaChatQuoteStage, reply: IaChatQuickReply): IaChatQuoteStage {
    switch (stage) {
        case 'taxesAsk':
            return reply === 'yes' ? 'taxesValue' : 'discountAsk';
        case 'discountAsk':
            return reply === 'yes' ? 'discountValue' : 'done';
        case 'taxesMode':
            return 'discountAsk';
        case 'discountMode':
            return 'done';
        default:
            return stage;
    }
}

/** Modo escolhido no botão (null quando o botão não é de modo). */
export function amountModeOf(reply: IaChatQuickReply): IaChatAmountMode | null {
    return reply === 'fixed' || reply === 'percentage' ? reply : null;
}

/** Chave i18n da pergunta de cada etapa. */
export function questionKeyOfStage(stage: IaChatQuoteStage): string {
    switch (stage) {
        case 'items':
            return 'iaChat.quoteItemsQuestion';
        case 'taxesAsk':
            return 'iaChat.quoteTaxesQuestion';
        case 'taxesValue':
            return 'iaChat.quoteTaxesValueQuestion';
        case 'taxesMode':
        case 'discountMode':
            return 'iaChat.quoteModeQuestion';
        case 'discountAsk':
            return 'iaChat.quoteDiscountQuestion';
        case 'discountValue':
            return 'iaChat.quoteDiscountValueQuestion';
        default:
            return 'iaChat.quoteScriptDone';
    }
}
